/**
 * Higgsfield CLI Effect Templates
 *
 * Each template defines a Higgsfield model + prompt + parameters that can be
 * applied to a photo (image-to-image) or used to generate a short video clip
 * starting from the photo. The backend Cloud Function `applyPhotoEffect`
 * receives the templateId + photoId and invokes the CLI accordingly.
 *
 * Image models  (e.g., nano_banana_2, gpt_image_2) -> output: still image
 * Video models  (e.g., kling3_0, seedance_2_0)     -> output: short MP4
 */

export type EffectKind = 'image' | 'video';

export interface EffectTemplate {
  id: string;
  name: string;
  description: string;
  kind: EffectKind;
  /** Higgsfield model identifier passed to `higgsfield generate create <model>` */
  model: string;
  /** Prompt template. Photo is supplied via --start-image / --image flag. */
  prompt: string;
  /** CLI flags (extra args) keyed by name -> value */
  flags: Record<string, string>;
  /** Emoji icon shown in picker */
  icon: string;
  /** Estimated processing duration shown to user */
  etaSeconds: number;
}

export const EFFECT_TEMPLATES: EffectTemplate[] = [
  {
    id: 'cinematic',
    name: 'Cinematic',
    description: 'Cinematic 35mm film look with rich color grading',
    kind: 'image',
    model: 'nano_banana_2',
    prompt:
      'cinematic 35mm film photograph, anamorphic lens flare, rich color grading, shallow depth of field, soft film grain, dramatic lighting',
    flags: { aspect_ratio: '16:9', resolution: '2k' },
    icon: '🎬',
    etaSeconds: 25,
  },
  {
    id: 'vintage',
    name: 'Vintage 1970s',
    description: 'Warm faded vintage film stock from the 70s',
    kind: 'image',
    model: 'nano_banana_2',
    prompt:
      'vintage 1970s analog film photo, warm faded tones, light leaks, soft grain, kodak portra style',
    flags: { aspect_ratio: '4:3', resolution: '2k' },
    icon: '📷',
    etaSeconds: 25,
  },
  {
    id: 'bw_dramatic',
    name: 'B&W Dramatic',
    description: 'High-contrast dramatic black and white',
    kind: 'image',
    model: 'nano_banana_2',
    prompt:
      'dramatic black and white photograph, high contrast, deep blacks, soft highlights, fine art monochrome',
    flags: { aspect_ratio: '4:3', resolution: '2k' },
    icon: '⚫',
    etaSeconds: 25,
  },
  {
    id: 'anime',
    name: 'Anime Style',
    description: 'Vibrant anime / manga illustration style',
    kind: 'image',
    model: 'nano_banana_2',
    prompt:
      'anime illustration, studio ghibli inspired, vibrant colors, cel shading, soft lighting',
    flags: { aspect_ratio: '1:1', resolution: '2k' },
    icon: '✨',
    etaSeconds: 25,
  },
  {
    id: 'oil_painting',
    name: 'Oil Painting',
    description: 'Impressionist oil painting with visible brushstrokes',
    kind: 'image',
    model: 'nano_banana_2',
    prompt:
      'classical oil painting, impressionist brushstrokes, rich pigments, museum quality artwork',
    flags: { aspect_ratio: '4:5', resolution: '2k' },
    icon: '🎨',
    etaSeconds: 30,
  },
  {
    id: 'neon_cyberpunk',
    name: 'Neon Cyberpunk',
    description: 'Futuristic neon cyberpunk aesthetic',
    kind: 'image',
    model: 'nano_banana_2',
    prompt:
      'cyberpunk aesthetic, vibrant neon lights, purple and pink glow, futuristic atmosphere, blade runner inspired',
    flags: { aspect_ratio: '16:9', resolution: '2k' },
    icon: '🌃',
    etaSeconds: 25,
  },
  {
    id: 'dreamy_bokeh',
    name: 'Dreamy Bokeh',
    description: 'Soft pastel dream with bokeh light',
    kind: 'image',
    model: 'nano_banana_2',
    prompt:
      'dreamy soft pastel color palette, beautiful bokeh background, ethereal glow, romantic mood',
    flags: { aspect_ratio: '3:4', resolution: '2k' },
    icon: '💫',
    etaSeconds: 25,
  },
  {
    id: 'studio_portrait',
    name: 'Studio Portrait',
    description: 'Professional studio portrait lighting',
    kind: 'image',
    model: 'nano_banana_2',
    prompt:
      'professional studio portrait, soft key light, neutral background, magazine cover quality, sharp focus',
    flags: { aspect_ratio: '4:5', resolution: '2k' },
    icon: '👤',
    etaSeconds: 25,
  },
  {
    id: 'kling_cinematic_motion',
    name: 'Cinematic Motion',
    description: 'Animate the photo into a 5-second cinematic clip',
    kind: 'video',
    model: 'kling3_0',
    prompt:
      'slow cinematic camera push-in, subtle parallax motion, gentle wind, film-like depth of field',
    flags: { duration: '5', mode: 'pro', sound: 'off' },
    icon: '🎥',
    etaSeconds: 90,
  },
  {
    id: 'seedance_slow_motion',
    name: 'Slow-Mo Wedding',
    description: 'Romantic slow-motion video starting from your photo',
    kind: 'video',
    model: 'seedance_2_0',
    prompt:
      'romantic slow motion wedding scene, soft golden hour light, gentle camera movement, cinematic depth',
    flags: { aspect_ratio: '16:9', duration: '5', resolution: '1080p', mode: 'std', genre: 'romance' },
    icon: '💍',
    etaSeconds: 120,
  },
];

export function getEffectTemplate(id: string): EffectTemplate | undefined {
  return EFFECT_TEMPLATES.find((t) => t.id === id);
}
